import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useSignal } from '@preact/signals';
import { useCallback, useEffect } from 'preact/hooks';
import { Trans, useTranslation } from 'react-i18next';
import '@etchteam/diamond-ui/composition/Collapse/Collapse';
import '@etchteam/diamond-ui/composition/FormGroup/FormGroup';
import '@etchteam/diamond-ui/control/Button/Button';
import '@etchteam/diamond-ui/control/Input/Input';
import '@/components/canvas/IconCircle/IconCircle';
import '@/components/composition/IconText/IconText';
import '@/components/content/Icon/Icon';
import LocatorApi from '@/lib/LocatorApi';
import { captureException } from '@/lib/sentry';
import useAnalytics from '@/lib/useAnalytics';
export default function ReportMissingMaterial({ missingMaterial, }) {
    const { t } = useTranslation();
    const tContext = 'components.reportMissingMaterial';
    const feedbackFormOpen = useSignal(false);
    const materialCategories = useSignal([]);
    const selectedCategory = useSignal(null);
    const showCategoryError = useSignal(false);
    const hasSubmittedFeedback = useSignal(false);
    const { recordEvent } = useAnalytics();
    useEffect(() => {
        // Reset the form state when the missing material changes
        feedbackFormOpen.value = false;
        selectedCategory.value = null;
        hasSubmittedFeedback.value = false;
    }, [missingMaterial]);
    const toggleFeedbackForm = useCallback(async () => {
        feedbackFormOpen.value = !feedbackFormOpen.value;
        if (materialCategories.value.length > 0) {
            return;
        }
        try {
            const materialCategoriesResponse = await LocatorApi.getInstance().get('material-categories');
            materialCategories.value = materialCategoriesResponse.map((category) => category.name);
        }
        catch (error) {
            captureException(error, { component: 'ReportMissingMaterial' });
        }
    }, []);
    const sendFeedback = useCallback(() => {
        if (!selectedCategory.value) {
            showCategoryError.value = true;
            return;
        }
        recordEvent({
            category: 'Material::Feedback',
            action: `Missing material: ${missingMaterial}, Suggested category: ${selectedCategory.value}`,
        });
        hasSubmittedFeedback.value = true;
        feedbackFormOpen.value = false;
    }, []);
    function handleCategoryChange(event) {
        selectedCategory.value = event.target.value;
        showCategoryError.value = false;
    }
    if (!missingMaterial) {
        return null;
    }
    return (_jsxs("locator-report-missing-material", { children: [_jsxs("locator-icon-text", { children: [_jsx("locator-icon-circle", { variant: hasSubmittedFeedback.value ? 'positive' : undefined, children: _jsx("locator-icon", { icon: hasSubmittedFeedback.value ? 'list-tick' : 'list-add', color: hasSubmittedFeedback.value ? undefined : 'primary' }) }), !hasSubmittedFeedback.value && (_jsx("p", { children: _jsx(Trans, { i18nKey: `${tContext}.title`, components: {
                                button: (_jsx("button", { type: "button", className: "locator-report-missing-material__toggle", onClick: toggleFeedbackForm, "aria-controls": "report-missing-material-collapse", "aria-expanded": feedbackFormOpen.value })),
                            } }) })), _jsx("output", { htmlFor: "suggested-category", children: hasSubmittedFeedback.value ? t(`${tContext}.confirmation`) : '' })] }), _jsxs("diamond-collapse", { id: "report-missing-material-collapse", open: feedbackFormOpen.value, children: [_jsxs("diamond-form-group", { className: "diamond-spacing-bottom-md", children: [_jsx("label", { htmlFor: "report-missing-material-category", className: "diamond-sr-only", children: t(`${tContext}.placeholder`) }), _jsxs("diamond-input", { state: showCategoryError.value ? 'invalid' : undefined, children: [_jsxs("select", { id: "report-missing-material-category", name: "suggested-category", disabled: !feedbackFormOpen.value, onChange: handleCategoryChange, "aria-invalid": showCategoryError.value, "aria-errormessage": !showCategoryError.value
                                            ? 'report-missing-material-category-error'
                                            : undefined, children: [_jsx("option", { value: "", children: t(`${tContext}.placeholder`) }), materialCategories.value.map((category) => (_jsx("option", { value: category, children: category }, category)))] }), _jsx("locator-icon", { icon: "expand" })] }), showCategoryError.value && (_jsx("p", { id: "report-missing-material-category-error", className: "text-color-negative diamond-text-size-sm diamond-spacing-top-xs", "aria-live": "polite", children: t(`${tContext}.error`) }))] }), _jsx("diamond-button", { width: "full-width", children: _jsx("button", { type: "button", onClick: sendFeedback, children: t(`${tContext}.cta`) }) })] })] }));
}
